import { useMemo, useCallback, memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ListCell } from '@cbhq/cds-web/cells';
import { VStack } from '@cbhq/cds-web/layout';
import { TextHeadline, TextBody } from '@cbhq/cds-web/typography';
import { ActionType, ComponentType } from '@cbhq/client-analytics';
import globalNavLogEvent from '../../../utils/globalNavLogEvent.js';
import { useRouter } from '../../RouterContext.js';
import { jsx, Fragment } from 'react/jsx-runtime';
const i18nKey = 'GlobalNavLanguageCountrySelector';
const messages = defineMessages({
  currentLanguageTitle: {
    id: `${i18nKey}.currentLanguageTitle`,
    defaultMessage: 'Current selected language',
    description: `
    #Component: LEGACY
    #CharLimit: 65
    #Context: Title for currently selected langauge list (CharLimit autocalculated to 65)`
  },
  otherSupportedLanguageTitle: {
    id: `${i18nKey}.otherSupportedLanguageTitle`,
    defaultMessage: 'Other supported languages',
    description: `
    #Component: LEGACY
    #CharLimit: 65
    #Context: Title for a section that lists other supported langauges (CharLimit autocalculated to 65)`
  }
});
function LanguageCountrySelector({
  localeInformation
}) {
  const supportedLocales = localeInformation.supportedLocales,
    localeCodeToDisplayName = localeInformation.localeCodeToDisplayName,
    selectedLocaleCode = localeInformation.selectedLocaleCode;
  const _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  const _useRouter = useRouter(),
    pathname = _useRouter.pathname;
  const languageNameMapToLocaleCode = useMemo(() => {
    const map = {};
    Object.keys(localeCodeToDisplayName).forEach(localeCode => {
      const languageName = localeCodeToDisplayName[localeCode];
      map[languageName] = localeCode;
    });
    return map;
  }, [localeCodeToDisplayName]);

  /**
   * This handleChange method is adopted from the one from LanguageSelector.tsx
   */
  const handleChange = useCallback(event => {
    const selectedLanguage = event.currentTarget.childNodes[0].textContent ?? 'English';
    globalNavLogEvent('logged_out_nav_language_selector_select', {
      action: ActionType.click,
      componentType: ComponentType.button,
      key: selectedLanguage
    });
    const localeCode = languageNameMapToLocaleCode[selectedLanguage];
    const subfolderRegEx = new RegExp(`^/(${supportedLocales.join('|')})(/|$)`);
    if (supportedLocales.includes(localeCode)) {
      const currentPath = pathname;
      let newPath = '';
      if (currentPath === '/') {
        newPath = `/${localeCode}`; // Special case for the home page
      } else if (currentPath.match(subfolderRegEx)) {
        newPath = currentPath.replace(subfolderRegEx, `/${localeCode}/`);
      } else {
        newPath = `/${localeCode}${currentPath}`;
      }
      newPath = newPath.replace(/\/$/, ''); // Remove trailing slash, if exists

      // Forces a reload, forcing the client to go through the Node server's locale cookie setting process
      window.location.replace(newPath);
    }
  }, [pathname, languageNameMapToLocaleCode, supportedLocales]);
  const currentLanguageName = localeCodeToDisplayName[selectedLocaleCode];
  const otherLanguageNames = useMemo(() => {
    const otherLanguages = [];
    Object.keys(localeCodeToDisplayName).forEach(localeCode => {
      const otherLangaugeName = localeCodeToDisplayName[localeCode];
      if (otherLangaugeName !== currentLanguageName) {
        otherLanguages.push(otherLangaugeName);
      }
    });
    return otherLanguages;
  }, [currentLanguageName, localeCodeToDisplayName]);
  const constructSelectionRows = useCallback(() => {
    const selectionRows = [];
    selectionRows.push({
      isTitle: true,
      content: /*#__PURE__*/jsx(TextHeadline, {
        as: "h1",
        children: formatMessage(messages.currentLanguageTitle)
      }),
      selected: false
    }, {
      isTitle: false,
      content: /*#__PURE__*/jsx(TextHeadline, {
        as: "h1",
        children: currentLanguageName
      }),
      selected: true
    }, {
      isTitle: true,
      content: /*#__PURE__*/jsx(TextHeadline, {
        as: "h1",
        children: formatMessage(messages.otherSupportedLanguageTitle)
      }),
      selected: false
    });
    otherLanguageNames.forEach(language => {
      selectionRows.push({
        isTitle: false,
        content: /*#__PURE__*/jsx(TextBody, {
          as: "p",
          children: language
        }),
        selected: false
      });
    });
    return selectionRows;
  }, [currentLanguageName, formatMessage, otherLanguageNames]);
  const languageSelectionRows = constructSelectionRows();
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(VStack, {
      width: "100%",
      children: languageSelectionRows.map((row, idx) => {
        return /*#__PURE__*/jsx(ListCell, {
          title: row.content,
          selected: row.selected,
          onPress: row.isTitle ? undefined : handleChange,
          outerSpacing: {
            spacingHorizontal: 0
          }
        }, `language-country-selector-row-${idx}`);
      })
    })
  });
}
const LanguageCountrySelector$1 = /*#__PURE__*/memo(LanguageCountrySelector);
export { LanguageCountrySelector$1 as default };
//# sourceMappingURL=LanguageCountrySelector.js.map
